// Phase II · Wave D.1 · pre-mount basename resolver.
//
// Reads window.location.pathname BEFORE React mounts. Decides the
// basename to pass to <BrowserRouter> so the entire app operates
// against a locale-prefixed URL space without having to enumerate
// 40 locales as routes.
//
// Side-effects:
//   · If the current pathname has no valid locale prefix AND is NOT a
//     skip path, we prepend the detected locale and update the URL
//     via history.replaceState (the user sees /it/dashboard instead
//     of /dashboard · one-time first-visit redirect).
//   · If the current pathname carries an INVALID candidate locale we
//     strip it and prepend the default locale.
//   · We set the ``glms_locale`` cookie as a side-effect only when we
//     actively detect a new locale (so manual cookie overrides persist).
//
// SSR safety: when ``window`` is undefined (server-side rendering
// path, Jest without jsdom) we return "" and leave URL handling to
// the regular legacy flow.
import Cookies from "js-cookie";

import {
    DEFAULT_LOCALE, GLMS_LOCALE_SET, LOCALE_COOKIE, LOCALE_COOKIE_TTL,
    isSkipPath, isValidLocale, normaliseLocale,
} from "./glms_locales";

// Memo: compute once per module load · React Strict Mode may invoke
// the component twice but the basename must remain stable.
let _cached = null;

export function getLocaleBasename() {
    if (_cached !== null) return _cached;
    if (typeof window === "undefined" || !window.location) {
        _cached = "";
        return _cached;
    }
    const path = window.location.pathname || "/";
    const search = window.location.search || "";
    const hash = window.location.hash || "";

    // Skip list: never prefix API, static, OAuth callbacks.
    if (isSkipPath(path)) {
        _cached = "";
        return _cached;
    }

    // Match leading /xx or /xx-XX segment (letters+dash only, 2-10 chars).
    const match = path.match(/^\/([A-Za-z]{2,3}(?:-[A-Za-z]{2,4})?)(\/|$)/);
    const candidate = match ? match[1] : null;

    if (candidate && GLMS_LOCALE_SET.has(candidate)) {
        // Valid locale prefix · nothing to rewrite.
        // Hotfix 2026-10 · the DEFAULT_LOCALE (en-US) MUST stay
        // prefix-less to align with web-ssr canonical URLs (English
        // has NO URL prefix in the SSR locale set). Strip the prefix
        // in-place when the user lands on `/en-US/<rest>`.
        if (candidate === DEFAULT_LOCALE) {
            const rest = path.slice(1 + candidate.length) || "/";
            try {
                window.history.replaceState(null, "", `${rest}${search}${hash}`);
            } catch (e) { /* no-op */ }
            _cached = "";
            return _cached;
        }
        _cached = `/${candidate}`;
        return _cached;
    }
    if (candidate && isValidLocale(candidate)) {
        // Case-tolerant / alias (e.g. "en" → "en-US").
        const canonical = normaliseLocale(candidate);
        const rest = path.slice(1 + candidate.length) || "/";
        try {
            window.history.replaceState(null, "", `/${canonical}${rest}${search}${hash}`);
        } catch (e) { /* no-op */ }
        _cached = `/${canonical}`;
        return _cached;
    }
    if (candidate && !isValidLocale(candidate) && _looksLikeLocaleToken(candidate)) {
        // User typed /xx/... but xx is not a known locale → send to EN.
        const rest = path.slice(1 + candidate.length) || "/";
        try {
            window.history.replaceState(null, "", `/${DEFAULT_LOCALE}${rest}${search}${hash}`);
            Cookies.set(LOCALE_COOKIE, DEFAULT_LOCALE, {
                expires: LOCALE_COOKIE_TTL, path: "/", sameSite: "lax", secure: true,
            });
        } catch (e) { /* no-op */ }
        _cached = `/${DEFAULT_LOCALE}`;
        return _cached;
    }

    // No prefix at all · detect preference and prepend.
    // Hotfix 2026-10 · when the detected locale IS the default
    // (en-US), stay prefix-less so client-side URLs match the
    // canonical SSR layout (English has no URL prefix). This also
    // prevents a feedback loop with the server-side /en-US/ 308
    // strip in setupProxy.js.
    const detected = detectLocale();
    if (detected === DEFAULT_LOCALE) {
        _cached = "";
        return _cached;
    }
    try {
        window.history.replaceState(null, "", `/${detected}${path}${search}${hash}`);
        Cookies.set(LOCALE_COOKIE, detected, {
            expires: LOCALE_COOKIE_TTL, path: "/", sameSite: "lax", secure: true,
        });
    } catch (e) { /* no-op */ }
    _cached = `/${detected}`;
    return _cached;
}

// Heuristic: a leading token "looks like" a locale candidate when it
// is 2-5 ASCII letters (optionally with a region tag). Keeps real
// app paths like "/dashboard" from being falsely stripped.
function _looksLikeLocaleToken(tok) {
    if (!tok) return false;
    return /^[A-Za-z]{2,3}(?:-[A-Za-z]{2,4})?$/.test(tok);
}

export function detectLocale() {
    // Priority: cookie → navigator.language → navigator.languages →
    // DEFAULT_LOCALE.
    try {
        const fromCookie = Cookies.get(LOCALE_COOKIE);
        if (isValidLocale(fromCookie)) return normaliseLocale(fromCookie);
    } catch (e) { /* no-op */ }
    try {
        const nav = navigator.language || "";
        if (isValidLocale(nav)) return normaliseLocale(nav);
        // Base-match: "it-CH" → "it" when "it" is in the set.
        const base = nav.split("-")[0];
        if (isValidLocale(base)) return normaliseLocale(base);
        if (Array.isArray(navigator.languages)) {
            for (const l of navigator.languages) {
                if (isValidLocale(l)) return normaliseLocale(l);
                const b = (l || "").split("-")[0];
                if (isValidLocale(b)) return normaliseLocale(b);
            }
        }
    } catch (e) { /* no-op */ }
    return DEFAULT_LOCALE;
}

// Test-only — reset the memoised basename so Jest tests can exercise
// multiple pathnames in one run.
export function __resetBasenameCacheForTests() {
    _cached = null;
}
